import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import SEO from '../components/SEO';
import { PageContainer } from '../components/PageHeader';
import CourseRequestForm from '../features/courses/components/CourseRequestForm';
import { useCreateCourseStream } from '../features/courses/hooks/useCreateCourseStream';
import { useStudentDetail } from '../features/parentPortal/hooks/useStudentDetail';
import { Skeleton, SkeletonPanel, SkeletonRegion } from '../components/ui/Skeleton';
import ErrorBlock from '../features/parentPortal/components/ErrorBlock';
import Button from '../features/parentPortal/components/Button';
import { pickLearningVerb } from '../utils/greeting';
import '../styles/parentPortal.css';

const STUDENT_SUB_ERROR = 'STUDENT_SUBSCRIPTION_REQUIRED';

// Maps the hook's streaming phase onto CourseRequestForm's status prop. See
// the identical helper in CourseRequestPage.jsx for the `dismissed` masking
// rationale.
function mapPhaseToStatus(phase) {
  if (phase === 'creating') return 'loading';
  if (phase === 'done') return 'success';
  return phase; // 'idle' | 'content_warning' | 'error' | 'connection_lost'
}

function buildErrorMessage(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error.code === 'RATE_LIMIT_EXCEEDED') {
    const secs = error?.data?.retryAfterSeconds;
    if (secs) {
      const mins = Math.max(1, Math.ceil(secs / 60));
      return `Too many requests. Please try again in ${mins} minute${mins === 1 ? '' : 's'}.`;
    }
    return error.message || 'Too many requests. Please try again later.';
  }
  return error.message || 'Failed to assign course. Please try again.';
}

export default function StudentAssignCoursePage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { enabled } = useAuth();
  const { student, loading, error, reload } = useStudentDetail(studentId);
  const { create, cancel, phase, stages, error: createError } = useCreateCourseStream({ studentId });

  const [dismissed, setDismissed] = useState(false);
  const [studentNeedsSubscription, setStudentNeedsSubscription] = useState(false);
  const lastPayloadRef = useRef(null);
  const navigatedRef = useRef(false);

  const studentFirstName = (student?.displayName || '').split(/\s+/)[0] || 'your student';
  const verb = useMemo(() => pickLearningVerb(), []);

  const handleSubmit = async (formValues) => {
    if (phase === 'creating' || phase === 'done') return;
    if (!formValues.title) return;

    const payload = {
      title: formValues.title,
      advancedSettings: formValues.advancedSettings,
    };

    lastPayloadRef.current = payload;
    navigatedRef.current = false;
    setDismissed(false);

    const res = await create({ payload, authEnabled: enabled });
    if (res.error?.code === STUDENT_SUB_ERROR) {
      setDismissed(true);
      setStudentNeedsSubscription(true);
    }
  };

  // Preserves the page's original post-assign destination, gated on the
  // real `complete` event instead of the old blocking call resolving.
  useEffect(() => {
    if (phase === 'done' && !navigatedRef.current) {
      navigatedRef.current = true;
      const t = setTimeout(() => navigate(`/family/students/${encodeURIComponent(studentId)}`), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [phase, studentId, navigate]);

  const handlePdfError = (err) => {
    if (err?.code === STUDENT_SUB_ERROR) {
      setStudentNeedsSubscription(true);
    }
  };

  const handleRetry = () => {
    if (lastPayloadRef.current) {
      handleSubmit(lastPayloadRef.current);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  const status = dismissed ? 'idle' : mapPhaseToStatus(phase);
  const errorMessage = status === 'content_warning'
    ? 'This topic may violate content guidelines. Please try a different topic.'
    : buildErrorMessage(createError);

  const handlePdfSuccessNavigate = () => {
    navigate(`/family/students/${encodeURIComponent(studentId)}`);
  };

  if (loading) {
    return (
      <PageContainer>
        <SkeletonRegion label="Loading student">
          <Skeleton width="46%" height={26} style={{ marginBottom: 'var(--space-3)' }} />
          <Skeleton width="66%" height={14} style={{ marginBottom: 'var(--space-6)' }} />
          <SkeletonPanel>
            <Skeleton height={92} style={{ marginBottom: 'var(--space-4)' }} />
            <Skeleton width={140} height={40} />
          </SkeletonPanel>
        </SkeletonRegion>
      </PageContainer>
    );
  }

  if (error || !student) {
    return (
      <PageContainer>
        <Link
          to="/family"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 'var(--space-4)',
          }}
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          Back to family
        </Link>
        <ErrorBlock message={error || 'Student not found.'} onRetry={reload} />
      </PageContainer>
    );
  }

  return (
    <>
      <SEO
        title={`Assign a course to ${student.displayName}`}
        path={`/family/students/${studentId}/assign`}
        noindex
      />
      <div style={{ position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            top: 4,
            left: 4,
            zIndex: 2,
          }}
        >
          <Link
            to={`/family/students/${encodeURIComponent(studentId)}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              color: 'var(--text-secondary)',
              textDecoration: 'none',
              fontSize: 13,
              fontWeight: 600,
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-control)',
              background: 'var(--glass-bg-medium)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Back to {studentFirstName}
          </Link>
        </div>

        {studentNeedsSubscription ? (
          <StudentNeedsSubscriptionBlock
            student={student}
            onBack={() => setStudentNeedsSubscription(false)}
          />
        ) : (
          <CourseRequestForm
            onSubmit={handleSubmit}
            status={status}
            errorMessage={errorMessage}
            onRetry={handleRetry}
            onDismiss={handleDismiss}
            stages={stages}
            onCancel={cancel}
            successSubtitle={`Taking you back to ${studentFirstName}...`}
            connectionLostHref={`/family/students/${encodeURIComponent(studentId)}`}
            connectionLostLabel={`Back to ${studentFirstName}`}
            heroTitle={`What's ${studentFirstName} ${verb} today?`}
            studentId={studentId}
            onPdfSuccessNavigate={handlePdfSuccessNavigate}
            onPdfError={handlePdfError}
          />
        )}
      </div>
    </>
  );
}

function StudentNeedsSubscriptionBlock({ student, onBack }) {
  return (
    <div
      className="pp-card"
      style={{ maxWidth: 560, margin: '96px auto var(--space-6)', padding: 'var(--space-6) var(--space-5)', textAlign: 'center' }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          margin: '0 auto var(--space-4)',
          borderRadius: 'var(--radius-panel)',
          background: 'var(--warning-bg)',
          color: 'var(--warning)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Lock size={24} strokeWidth={1.5} />
      </div>
      <h2
        style={{
          margin: '0 0 var(--space-3)',
          fontSize: 'var(--text-section-size)',
          fontWeight: 'var(--text-section-weight)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--text-section-tracking)',
        }}
      >
        {student?.displayName || 'This student'} needs an active subscription
      </h2>
      <p
        style={{
          margin: '0 auto var(--space-6)',
          maxWidth: 440,
          fontSize: 'var(--text-body-size)',
          color: 'var(--text-secondary)',
          lineHeight: 1.6,
        }}
      >
        Courses are generated on the student's account, so their subscription is what pays for the AI. Set one up on their account, then come back to assign.
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
        <a
          href="/pricing"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <Button>Open pricing page →</Button>
        </a>
      </div>
    </div>
  );
}
